import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { UserCheck, UserX, Ban, RefreshCw, CheckCircle, Clock, XCircle, Building2, Mail, Phone, Pencil } from 'lucide-react';
import { superuserService } from '../../services/api';

interface Gerant {
  id: number;
  email: string;
  nom: string;
  prenom: string;
  telephone: string | null;
  status: string;
  created_at: string;
  apartments_count: number;
}

const GerantsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [gerants, setGerants] = useState<Gerant[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [rejectModal, setRejectModal] = useState<{ open: boolean; gerantId: number | null; reason: string }>({
    open: false,
    gerantId: null,
    reason: ''
  });
  const [editModal, setEditModal] = useState<{ open: boolean; gerant: Gerant | null }>({
    open: false,
    gerant: null
  });
  const [editForm, setEditForm] = useState({ nom: '', prenom: '', email: '', telephone: '' });

  const statusFilter = searchParams.get('status') || '';

  const fetchGerants = async () => {
    setLoading(true);
    try {
      const response = await superuserService.getGerants(statusFilter || undefined);
      if (response.data?.gerants) {
        setGerants(response.data.gerants);
      }
    } catch (error: any) {
      console.error('Erreur chargement gérants:', error.response?.data || error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGerants();
  }, [statusFilter]);

  const handleApprove = async (gerantId: number) => {
    setActionLoading(gerantId);
    try {
      await superuserService.approveGerant(gerantId);
      await fetchGerants();
    } catch (error: any) {
      console.error('Erreur approbation:', error.response?.data || error);
      alert(error.response?.data?.error || 'Erreur lors de l\'approbation');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!rejectModal.gerantId) return;
    setActionLoading(rejectModal.gerantId);
    try {
      await superuserService.rejectGerant(rejectModal.gerantId, rejectModal.reason);
      setRejectModal({ open: false, gerantId: null, reason: '' });
      await fetchGerants();
    } catch (error: any) {
      console.error('Erreur rejet:', error.response?.data || error);
      alert(error.response?.data?.error || 'Erreur lors du rejet');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSuspend = async (gerantId: number) => {
    if (!window.confirm('Voulez-vous vraiment suspendre ce gérant ?')) return;
    setActionLoading(gerantId);
    try {
      await superuserService.suspendUser(gerantId);
      await fetchGerants();
    } catch (error: any) {
      console.error('Erreur suspension:', error.response?.data || error);
      alert(error.response?.data?.error || 'Erreur lors de la suspension');
    } finally {
      setActionLoading(null);
    }
  };

  const handleActivate = async (gerantId: number) => {
    setActionLoading(gerantId);
    try {
      await superuserService.activateUser(gerantId);
      await fetchGerants();
    } catch (error: any) {
      console.error('Erreur activation:', error.response?.data || error);
      alert(error.response?.data?.error || 'Erreur lors de l\'activation');
    } finally {
      setActionLoading(null);
    }
  };

  const openEditModal = (gerant: Gerant) => {
    setEditForm({
      nom: gerant.nom,
      prenom: gerant.prenom,
      email: gerant.email,
      telephone: gerant.telephone || ''
    });
    setEditModal({ open: true, gerant });
  };

  const handleUpdateGerant = async () => {
    if (!editModal.gerant) return;
    setActionLoading(editModal.gerant.id);
    try {
      await superuserService.updateUser(editModal.gerant.id, editForm);
      setEditModal({ open: false, gerant: null });
      await fetchGerants();
    } catch (error: any) {
      console.error('Erreur mise à jour:', error.response?.data || error);
      alert(error.response?.data?.error || 'Erreur lors de la mise à jour');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'validated':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"><CheckCircle className="w-3 h-3" /> Validé</span>;
      case 'pending_admin_approval':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800"><Clock className="w-3 h-3" /> En attente</span>;
      case 'suspended':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800"><XCircle className="w-3 h-3" /> Suspendu</span>;
      case 'pending':
        return <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"><Clock className="w-3 h-3" /> Email non validé</span>;
      default:
        return <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-heading font-bold text-neutral-900">Gestion des Gérants</h1>
          <p className="text-neutral-600">Validez ou gérez les comptes gérants</p>
        </div>
        <button onClick={fetchGerants} className="btn btn-outline flex items-center gap-2">
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      {/* Filtres */}
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setSearchParams({})}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
            !statusFilter ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Tous
        </button>
        <button
          onClick={() => setSearchParams({ status: 'pending_admin_approval' })}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
            statusFilter === 'pending_admin_approval' ? 'bg-orange-500 text-white' : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
          }`}
        >
          En attente
        </button>
        <button
          onClick={() => setSearchParams({ status: 'validated' })}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
            statusFilter === 'validated' ? 'bg-green-500 text-white' : 'bg-green-100 text-green-700 hover:bg-green-200'
          }`}
        >
          Validés
        </button>
        <button
          onClick={() => setSearchParams({ status: 'suspended' })}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
            statusFilter === 'suspended' ? 'bg-red-500 text-white' : 'bg-red-100 text-red-700 hover:bg-red-200'
          }`}
        >
          Suspendus
        </button>
      </div>

      {/* Liste des gérants */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      ) : gerants.length === 0 ? (
        <div className="card p-12 text-center">
          <p className="text-neutral-500">Aucun gérant trouvé</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {gerants.map((gerant) => (
            <div key={gerant.id} className={`card p-6 ${gerant.status === 'pending_admin_approval' ? 'border-2 border-orange-300 bg-orange-50' : ''}`}>
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-lg">{gerant.prenom} {gerant.nom}</h3>
                    {getStatusBadge(gerant.status)}
                  </div>
                  <div className="space-y-1 text-sm text-neutral-600">
                    <p className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {gerant.email}
                    </p>
                    {gerant.telephone && (
                      <p className="flex items-center gap-2">
                        <Phone className="w-4 h-4" />
                        {gerant.telephone}
                      </p>
                    )}
                    <p className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      {gerant.apartments_count} appartement{gerant.apartments_count > 1 ? 's' : ''}
                    </p>
                    <p className="text-xs text-neutral-400">
                      Inscrit le {formatDate(gerant.created_at)}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => openEditModal(gerant)}
                    className="btn bg-blue-100 text-blue-700 hover:bg-blue-200 flex items-center gap-2"
                  >
                    <Pencil className="w-4 h-4" />
                    Modifier
                  </button>
                  {gerant.status === 'pending_admin_approval' && (
                    <>
                      <button
                        onClick={() => handleApprove(gerant.id)}
                        disabled={actionLoading === gerant.id}
                        className="btn bg-green-500 text-white hover:bg-green-600 flex items-center gap-2"
                      >
                        {actionLoading === gerant.id ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <UserCheck className="w-4 h-4" />
                        )}
                        Approuver
                      </button>
                      <button
                        onClick={() => setRejectModal({ open: true, gerantId: gerant.id, reason: '' })}
                        disabled={actionLoading === gerant.id}
                        className="btn bg-red-500 text-white hover:bg-red-600 flex items-center gap-2"
                      >
                        <UserX className="w-4 h-4" />
                        Rejeter
                      </button>
                    </>
                  )}
                  {gerant.status === 'validated' && (
                    <button
                      onClick={() => handleSuspend(gerant.id)}
                      disabled={actionLoading === gerant.id}
                      className="btn bg-red-100 text-red-700 hover:bg-red-200 flex items-center gap-2"
                    >
                      {actionLoading === gerant.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Ban className="w-4 h-4" />
                      )}
                      Suspendre
                    </button>
                  )}
                  {gerant.status === 'suspended' && (
                    <button
                      onClick={() => handleActivate(gerant.id)}
                      disabled={actionLoading === gerant.id}
                      className="btn bg-green-100 text-green-700 hover:bg-green-200 flex items-center gap-2"
                    >
                      {actionLoading === gerant.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4" />
                      )}
                      Réactiver
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de rejet */}
      {rejectModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Rejeter l'inscription</h3>
            <p className="text-neutral-600 mb-4">Veuillez indiquer la raison du rejet (optionnel) :</p>
            <textarea
              value={rejectModal.reason}
              onChange={(e) => setRejectModal({ ...rejectModal, reason: e.target.value })}
              className="w-full border border-gray-300 rounded-lg p-3 mb-4"
              rows={3}
              placeholder="Raison du rejet..."
            />
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setRejectModal({ open: false, gerantId: null, reason: '' })}
                className="btn btn-outline"
              >
                Annuler
              </button>
              <button
                onClick={handleReject}
                disabled={actionLoading !== null}
                className="btn bg-red-500 text-white hover:bg-red-600"
              >
                {actionLoading ? 'Traitement...' : 'Confirmer le rejet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'édition */}
      {editModal.open && editModal.gerant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Modifier le gérant</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                <input
                  type="text"
                  value={editForm.prenom}
                  onChange={(e) => setEditForm({ ...editForm, prenom: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg p-3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                <input
                  type="text"
                  value={editForm.nom}
                  onChange={(e) => setEditForm({ ...editForm, nom: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg p-3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg p-3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input
                  type="tel"
                  value={editForm.telephone}
                  onChange={(e) => setEditForm({ ...editForm, telephone: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg p-3"
                />
              </div>
            </div>
            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => setEditModal({ open: false, gerant: null })}
                className="btn btn-outline"
              >
                Annuler
              </button>
              <button
                onClick={handleUpdateGerant}
                disabled={actionLoading !== null}
                className="btn btn-primary"
              >
                {actionLoading ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GerantsPage;
